// Harbours (taxgw-31ba; baked by tools/data/harbours.mjs): which cities are ports, and the channel each port's ships
// sail to open water. A city is a port exactly when it has a harbour: real ocean within the bake's coast distance that
// joins navigable sea. The channel runs from the coast point nearest the city (first) through real water to the first
// point inside navigable 0.25° land-mask water (last); the sim carves every channel into its sea grid like a sea
// passage, so the only land a ship crosses is the approach from the city to its coast point. Validated once at module
// load: bad data throws ContentValidationError.

import harboursJson from '../data/harbours.json'
import { ContentValidationError } from './errors'
import { CITY_BY_ID, LAND_MASK, type CityId } from './geo'
import type { LatLon } from './geometry/routes'

export interface Harbour {
  readonly city: CityId
  /** [lat, lon] from the coast point nearest the city to navigable sea water; one point when that coast is open sea. */
  readonly channel: readonly LatLon[]
}

/** The rules the harbours were baked with (tools/data/harbours.mjs), shipped with the data. */
export const HARBOUR_BAKE: Readonly<typeof harboursJson.bake> = harboursJson.bake

const HARBOURS_VERSION = 1
const EARTH_RADIUS_KM = 6371
const DEG_TO_RAD = Math.PI / 180
const KM_PER_DEG = EARTH_RADIUS_KM * DEG_TO_RAD
/** The coast point is a fine raster cell centre measured from the city's own cell: allow two cells past the distance. */
const COAST_POINT_SLACK_KM = 2 * HARBOUR_BAKE.rasterDeg * KM_PER_DEG

function fail(message: string): never {
  throw new ContentValidationError(`harbours: ${message}`)
}

function greatCircleKm(latA: number, lonA: number, latB: number, lonB: number): number {
  const a = Math.sin(((latB - latA) * DEG_TO_RAD) / 2) ** 2 + Math.cos(latA * DEG_TO_RAD) * Math.cos(latB * DEG_TO_RAD) * Math.sin(((lonB - lonA) * DEG_TO_RAD) / 2) ** 2
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(a)))
}

function loadHarbours(): ReadonlyMap<CityId, Harbour> {
  if (harboursJson.version !== HARBOURS_VERSION) fail(`version ${harboursJson.version}, expected ${HARBOURS_VERSION}`)
  const harbours = new Map<CityId, Harbour>()
  for (const raw of harboursJson.harbours) {
    const city = CITY_BY_ID.get(raw.id as CityId) ?? fail(`unknown city ${raw.id}`)
    if (harbours.has(city.id)) fail(`city ${city.name} has two harbours`)
    if (raw.points.length === 0) fail(`${city.name}'s harbour has no points`)
    const channel = raw.points.map((point): LatLon => {
      const [lat, lon] = point
      if (point.length !== 2 || lat === undefined || lon === undefined || !Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) {
        fail(`${city.name}'s harbour has an invalid point`)
      }
      return [lat, lon]
    })
    const [coastLat, coastLon] = channel[0] as LatLon
    if (greatCircleKm(city.lat, city.lon, coastLat, coastLon) > HARBOUR_BAKE.coastKm + COAST_POINT_SLACK_KM) fail(`${city.name}'s coast point is farther than ${HARBOUR_BAKE.coastKm} km`)
    const [seaLat, seaLon] = channel[channel.length - 1] as LatLon
    if (LAND_MASK.isLand(seaLat, seaLon)) fail(`${city.name}'s harbour channel ends on land-mask land`)
    harbours.set(city.id, { city: city.id, channel })
  }
  return harbours
}

/** Every port's harbour, by city id; a city without one is not coastal (no Port, no ships, no ferry stop). */
export const HARBOURS: ReadonlyMap<CityId, Harbour> = loadHarbours()
